import { GetApp } from "../App"
import { EditHistoryEntry, formatEditValue, groupEditHistory } from "../App/EditHistory"
import { applyFieldFormatEntry, FieldFormat } from "../App/Format"
import { GetShipUsers } from "../Views/ShipView"
import { classNames } from "../Components"
import { Block, BlockField, BlockRow } from "./Block"

// Shared "modifications" table for a record's field-level edit history (issue #529),
// used by both LogView (`.log-details`'s sibling section) and Day/EditView. Fully
// controlled by the caller via `open` (issue #1169) -- the caller's own "last
// edited" line is the only expand/collapse affordance now, so this component has
// no toggle or count of its own. Renders a single shared header row
// (field/previous/new/changed, rendered once) followed by one row per
// `editGroupId` -- every field changed together in a single save, or across separate
// nearby saves by the same user the server decided to bucket together (see
// `AuditLogEdit.reusableEditGroupId`, issue #529), shares one row, with each changed
// field rendered as its own stacked line within that row's field/previous/new cells
// (deduplicated per field by `groupEditHistory`), and one shared "changed" (date +
// editor) cell for the whole group. Column widths are fixed via CSS Grid
// (`EditHistory.scss`) so they line up identically between the header and every data
// row, regardless of content.
export interface EditHistoryProps {
  entries: EditHistoryEntry[];
  // Resolves a field name (e.g. "heading") to its display label. Field vocabularies
  // differ entirely between record kinds (Log vs Day), so this stays the caller's
  // responsibility rather than something this shared component could hardcode.
  fieldLabel: (field: string) => string;
  // Per-field value formatters (issue #529), e.g. `LogViewData.fieldFormat(app)` --
  // reused from the record's own input-field rendering, so a previous/new value here
  // always matches what that (possibly now-superseded) input field itself would have
  // shown. Each entry is either a `FormatKind` name (dispatched via `app.format`) or
  // a `CompositeFormatter` function; a field with no entry (or when this prop is
  // omitted entirely, e.g. in simpler catalog fixtures) falls back to
  // `formatEditValue`'s generic stringifier.
  fieldFormat?: FieldFormat;
  // Whether the table is expanded. Fully controlled by the caller (issue #1169) --
  // the caller owns the expand/collapse state (driven by its "last edited" line)
  // and also decides whether to show this component at all based on the ship's
  // `EditPermissivenessLevel`.
  open: boolean;
  className?: string;
}

export function EditHistory({ entries, fieldLabel, fieldFormat, open, className }: EditHistoryProps) {
  const app = GetApp();
  const t = app.getT("EditHistory");
  const shipUsers = GetShipUsers();

  if (entries.length < 1 || !open) {
    return null;
  }

  const groups = groupEditHistory(entries);
  const formatValue = (field: string, value: unknown): string => {
    const entry = fieldFormat?.[field];
    return entry == undefined ? formatEditValue(value) : applyFieldFormatEntry(entry, value, app.format);
  };

  return (
    <Block className={classNames(["EditHistory", className])}>
      <div className="EditHistory-title">{t("title")}</div>
      <div className="EditHistory-table">
        <BlockRow className="EditHistory-header">
          <BlockField className="EditHistory-field bold">{t("field")}</BlockField>
          <BlockField className="EditHistory-previous bold">{t("previous")}</BlockField>
          <BlockField className="EditHistory-new bold">{t("new")}</BlockField>
          <BlockField className="EditHistory-meta bold">{t("changed")}</BlockField>
        </BlockRow>
        {groups.map((group) => {
          const editor = shipUsers?.find((u) => u.id == group.shipUserId);
          return (
            <BlockRow key={group.editGroupId} className="EditHistory-row">
              <BlockField className="EditHistory-field">
                {group.fields.map((f) => <div key={f.field}>{fieldLabel(f.field)}</div>)}
              </BlockField>
              <BlockField className="EditHistory-previous">
                {group.fields.map((f) => <div key={f.field}>{formatValue(f.field, f.previousValue)}</div>)}
              </BlockField>
              <BlockField className="EditHistory-new">
                {group.fields.map((f) => <div key={f.field}>{formatValue(f.field, f.newValue)}</div>)}
              </BlockField>
              <BlockField className="EditHistory-meta">{t("entry_meta", { date: app.format("dateAndTime", group.editedAt), name: editor?.name || editor?.shortname || "" })}</BlockField>
            </BlockRow>
          );
        })}
      </div>
    </Block>
  );
}
